import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ui/Form";
import NumberInput from "@ui/NumberInput";
import { useMultiStepForm } from "@/hooks";
import type { Step4FormKeys } from "@/utils/step4Schema";
import YesNoRadio from "@/components/YesNoRadio";
import type { StepProps } from "../StepProps";
import PVSubform from "./PVSubform";

const fields: Step4FormKeys[] = ["installationPower", "inverterPower", "voivodeship"];

export const Step4: FC<StepProps> = ({ additionalItems }) => {
  const { t } = useTranslation();
  const { form } = useMultiStepForm();

  const clearPVErrors = () => {
    const options = {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    };

    fields.forEach(f => {
      form.setValue(`step4.${f}`, "", options);
      form.clearErrors(`step4.${f}`);
    });
  };

  return (
    <>
      <div className="space-y-4" data-test="power-section">
        <p className="text-base font-bold">{t("step4.q1")}</p>
        <p className="sm:px-4` text-sm font-light">
          <strong>{t("step4.connectionPowerOverrideHintImportant")}</strong>{" "}
          {t("step4.connectionPowerOverrideHint")}
        </p>
        <div className="flex flex-col gap-4 sm:px-4" data-test="power-inputs">
          <FormField
            control={form.control}
            name="step4.importPower"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="space-y-0.5">
                    <FormDescription>{t("step4.importPower")}</FormDescription>
                    <NumberInput
                      {...field}
                      dataTest="import-power-input"
                      isError={Boolean(form.formState.errors.step3?.fixedPrice)}
                      className="h-10 sm:max-w-[172px]"
                      unit={t("MW")}
                    />
                  </div>
                </FormControl>
                <FormMessage data-test="import-power-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="step4.exportPower"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="space-y-0.5">
                    <FormDescription>{t("step4.exportPower")}</FormDescription>
                    <NumberInput
                      {...field}
                      dataTest="export-power-input"
                      isError={Boolean(form.formState.errors.step4?.exportPower)}
                      className="h-10 sm:max-w-[172px]"
                      unit={t("MW")}
                    />
                    <FormMessage data-test="export-power-error" />
                    <p className="mt-1 text-sm font-light">{t("step4.exportPowerHint")}</p>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className="space-y-4" data-test="sell-energy-section">
        <FormLabel>{t("step4.q2")}</FormLabel>
        <FormField
          control={form.control}
          name="step4.canSellEnergy"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <YesNoRadio
                  value={field.value}
                  onChange={field.onChange}
                  id="q2"
                  dataTestPrefix="can-sell"
                />
              </FormControl>
              <FormMessage data-test="can-sell-error" />
            </FormItem>
          )}
        />
      </div>

      <div className="space-y-4" data-test="pv-section">
        <FormLabel>{t("step4.q3")}</FormLabel>
        <div className="space-y-2 sm:px-4" data-test="pv-question">
          <FormField
            control={form.control}
            name="step4.hasPvInstallation"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <YesNoRadio
                    value={field.value}
                    onChange={value => {
                      field.onChange(value);
                      if (value === "false") {
                        clearPVErrors();
                      }
                    }}
                    id="q3"
                    className="sm:px-0"
                    dataTestPrefix="has-pv"
                  />
                </FormControl>
                <FormMessage data-test="has-pv-error" />
              </FormItem>
            )}
          />
          <PVSubform />
        </div>
        {additionalItems}
      </div>
    </>
  );
};
